import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react';

interface InternalDebugContext {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
}

const InternalDebugContext = createContext<InternalDebugContext>(
	null as unknown as InternalDebugContext
);

export function useKonamiCode(): boolean {
	const { isOpen, setIsOpen } = useContext(InternalDebugContext);

	// Konami code
	useEffect(() => {
		const keyPattern = [
			'ArrowUp',
			'ArrowUp',
			'ArrowDown',
			'ArrowDown',
			'ArrowLeft',
			'ArrowRight',
			'ArrowLeft',
			'ArrowRight',
		];

		let currentIdx = 0;

		const handler = (e: DocumentEventMap['keydown']) => {
			if (!keyPattern.includes(e.key) || e.key !== keyPattern[currentIdx]) {
				currentIdx = 0;
				return;
			}

			currentIdx += 1;

			if (keyPattern.length === currentIdx) {
				currentIdx = 0;

				setIsOpen(!isOpen);
			}
		};

		document.addEventListener('keydown', handler);

		return () => {
			document.removeEventListener('keydown', handler);
		};
	}, [isOpen]);

	return isOpen;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// export function useDebugValues<T extends Record<string, any>>(sectionName: string, schema?: UseDebugValuesSchema): T {
//     const { isOpen, setIsOpen } = useContext(InternalDebugContext);
//
//     // TODO: watch storage, save/load localStorage
//
//     // Konami code
//     useEffect(() => {
//         const keyPattern = [
//             'ArrowUp',
//             'ArrowUp',
//             'ArrowDown',
//             'ArrowDown',
//             'ArrowLeft',
//             'ArrowRight',
//             'ArrowLeft',
//             'ArrowRight',
//         ];
//
//         let currentIdx = 0;
//
//         const handler = (e: DocumentEventMap['keydown']) => {
//             if (!keyPattern.includes(e.key) || e.key !== keyPattern[currentIdx]) {
//                 currentIdx = 0;
//                 return;
//             }
//
//             currentIdx += 1;
//
//             if (keyPattern.length === currentIdx) {
//                 currentIdx = 0;
//
//                 setIsOpen(true);
//             }
//         };
//
//         document.addEventListener('keydown', handler);
//
//         return () => {
//             document.removeEventListener('keydown', handler);
//         };
//     }, []);
//
//     return null as unknown as T;
// }

interface DebugValuesState {
	isOpen: boolean;
}

enum ACTION {
	OPEN_MENU,
	CLOSE_MENU,
}

type Action = { type: ACTION };

const debugValuesReducer = (state: DebugValuesState, action: Action): DebugValuesState => {
	switch (action.type) {
		case ACTION.OPEN_MENU:
			return {
				...state,
				isOpen: true,
			};
		case ACTION.CLOSE_MENU:
			return {
				...state,
				isOpen: false,
			};
		default:
			return state;
	}
};

const initialState: DebugValuesState = {
	isOpen: false,
};

export const DebugValuesProvider = ({ children }: PropsWithChildren) => {
	// d.skuza, a.wrobel - as for now this seems unnecessary but when we will add here
	//                     debug menu it will be used
	const [state, dispatch] = useReducer(debugValuesReducer, initialState);

	const setIsOpen = useCallback((isOpen: boolean) => {
		if (isOpen) {
			dispatch({ type: ACTION.OPEN_MENU });
		} else {
			dispatch({ type: ACTION.CLOSE_MENU });
		}
	}, []);

	const value = useMemo((): InternalDebugContext => {
		return {
			setIsOpen,
			isOpen: state.isOpen,
		};
	}, [state.isOpen]);

	return <InternalDebugContext.Provider value={value}>{children}</InternalDebugContext.Provider>;
};
